<template>
  <b-table-simple
    hover
    caption-top
    responsive
    bordered
    class="master-personnel-table"
  >
    <b-thead>
      <b-tr>
        <b-th
          v-for="(header , index) in headerData"
          :key="index"
          class="personnel-table text-center align-middle"
          :style="getHeaderStyle(header)"
        >
          {{ header }}
        </b-th>
      </b-tr>
    </b-thead>

    <b-tbody>
      <template v-for="main in mainCols">
        <b-tr
          v-for="(user, index) in groupedUsers[main.id]"
          :key="main.id + '-' + (user.userId || index) + '-' + index"
          :class="{ 'border-bottom-double-line': index === groupedUsers[main.id].length - 1 }"
        >
          <!-- 1. Certificate Category -->
          <b-th
            v-if="index === 0 && getHeaderCols('Certificate Category')"
            :rowspan="groupedUsers[main.id].length"
            class="align-middle text-center"
            style="width: 120px; min-width: 110px; max-width: 130px; white-space: normal !important; word-wrap: break-word !important;"
          >
            {{ main.name }}
          </b-th>

          <!-- 2. Eng Name -->
          <b-td
            v-if="getHeaderCols('Eng Name')"
            class="hover-pointer align-middle text-center text-nowrap"
            style="white-space: nowrap !important; vertical-align: middle !important;"
            @click="$router.push({ name: 'user-personnel-evaluation', params: { id: user.userId , ccId: main.id } })"
          >
            {{ user.engName || 'N/A' }}
          </b-td>

          <!-- 3. Evaluation Final: Fitted width (min-width: 320px) single-line -->
          <b-td
            v-if="getHeaderCols('Evaluation Final')"
            class="align-middle text-center text-nowrap"
            style="white-space: nowrap !important; vertical-align: middle !important; min-width: 320px;"
          >
            <span
              :id="'eval-final-' + main.id + '-' + index"
              class="hover-pointer font-weight-bold text-nowrap"
              style="white-space: nowrap !important; display: inline-block;"
            >
              {{ user.evaluationFinal ? user.evaluationFinal : 'N/A' }}
            </span>
            <b-popover
              v-if="user.allGrantedLevels && user.allGrantedLevels.length"
              :target="'eval-final-' + main.id + '-' + index"
              triggers="hover focus"
              placement="top"
              :title="'Granted Levels — ' + (user.engName || '')"
            >
              <ul class="pl-1 mb-0 list-unstyled text-left">
                <li
                  v-for="(lvl, idx) in user.allGrantedLevels"
                  :key="idx"
                  class="py-25 font-small-3 text-dark"
                >
                  • {{ lvl }}
                </li>
              </ul>
            </b-popover>
          </b-td>

          <!-- 4. Evaluation Date -->
          <b-td
            v-if="getHeaderCols('Evaluation Date')"
            class="align-middle text-center text-nowrap"
            style="white-space: nowrap !important; vertical-align: middle !important;"
          >
            {{ formatDate(user.lastEvaluationDate) }}
          </b-td>

          <!-- 5. Next Evaluation -->
          <b-td
            v-if="getHeaderCols('Next Evaluation')"
            class="align-middle text-center text-nowrap"
            style="white-space: nowrap !important; vertical-align: middle !important;"
            :style="{ color: isAfterToday(user.nextEvaluationDate) ? 'red' : '' }"
          >
            {{ formatDate(user.nextEvaluationDate) }}
          </b-td>

          <!-- 6. Evaluator -->
          <b-td
            v-if="getHeaderCols('Evaluator')"
            class="align-middle text-center text-nowrap"
            style="white-space: nowrap !important; vertical-align: middle !important;"
          >
            {{ user.evaluators ? (typeof user.evaluators === 'object' ? user.evaluators.name : user.evaluators) : 'N/A' }}
          </b-td>

          <!-- 7. Branch (After Evaluator!) -->
          <b-td
            v-if="getHeaderCols('Branch')"
            class="align-middle text-center text-nowrap"
            style="white-space: nowrap !important; vertical-align: middle !important;"
          >
            {{ user.branch ? (typeof user.branch === 'object' ? user.branch.name : user.branch) : 'N/A' }}
          </b-td>

          <!-- 8. Sources (In the end!) -->
          <b-td
            v-if="getHeaderCols('Sources')"
            class="align-middle text-center text-nowrap"
            style="white-space: nowrap !important; vertical-align: middle !important;"
          >
            <div
              v-if="user.source && user.source.length"
              style="white-space: nowrap !important;"
            >
              <span
                v-for="(src, i) in user.source"
                :key="i"
                style="white-space: nowrap !important;"
              >
                {{ src }}<span v-if="i !== user.source.length - 1">, </span>
              </span>
            </div>
            <div v-else>
              N/A
            </div>
          </b-td>

          <!-- 9. Active (External Inspectors only) -->
          <b-td
            v-if="getHeaderCols('Active')"
            class="align-middle text-center text-nowrap"
            style="white-space: nowrap !important; vertical-align: middle !important;"
          >
            <b-badge :variant="user.isActive ? 'light-success' : 'light-secondary'">
              {{ user.isActive ? 'Active' : 'Inactive' }}
            </b-badge>
          </b-td>
        </b-tr>
      </template>
    </b-tbody>
  </b-table-simple>
</template>

<script>
export default {
  props: {
    mainCols: {
      type: Array,
      default: () => [],
    },
    groupedUsers: {
      type: Object,
      default: () => {},
    },
    headerData: {
      type: Array,
      default: () => [],
    },
  },
  methods: {
    getHeaderStyle(header) {
      if (header === 'Certificate Category') {
        return {
          width: '120px',
          minWidth: '110px',
          maxWidth: '130px',
          whiteSpace: 'normal !important',
          wordBreak: 'break-word !important',
          verticalAlign: 'middle !important',
        }
      }
      if (header === 'Evaluation Final') {
        return {
          minWidth: '320px',
          whiteSpace: 'nowrap !important',
          verticalAlign: 'middle !important',
        }
      }
      return {
        whiteSpace: 'nowrap !important',
        verticalAlign: 'middle !important',
      }
    },
    formatDate(date) {
      if (!date) return 'N/A'
      return date.slice(0, 10)
    },
    getHeaderCols(header) {
      return this.headerData.find(item => item === header)
    },
    isAfterToday(date) {
      if (!date) return false
      const today = new Date()
      const d = new Date(date)
      if (Number.isNaN(d.getTime())) return false
      today.setHours(0, 0, 0, 0)
      d.setHours(0, 0, 0, 0)
      return d < today
    },
  },
}
</script>

<style scoped>
.master-personnel-table ::v-deep td {
  vertical-align: middle !important;
}
.border-bottom-double-line {
  border-bottom: 3px double #b8c2cc !important;
}
::v-deep .popover-header {
  color: #ffffff !important;
  font-weight: 700 !important;
  font-size: 0.875rem !important;
}
</style>
